import { useEffect, useState } from 'react';
import { CheckSquare, Repeat } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { taskAutomationAPI, TASK_STATUS, shortDate } from '../../lib/taskTemplatesAPI';
import { TaskProgress } from './ChecklistEditor';
import { TaskFilters, GROUPS } from './TaskFilters';

const STATUS = Object.fromEntries(TASK_STATUS.map((s) => [s.id, s.title]));
const groupKey = (t, g) => ({ template: t.template_snapshot?.name || 'No template', client: t.client_name || 'No client', assignee: t.assignee_name || 'Unassigned',
  status: STATUS[t.status] || t.status, period: t.reporting_period_label || 'No period', financial_year: t.financial_year ? `FY${t.financial_year}` : 'No FY' }[g]);

export const DueDates = ({ task }) => (
  <span className="text-xs text-muted-foreground" data-testid={`task-due-${task.id}`}>
    <span className="whitespace-nowrap">{task.internal_due_date ? `Due ${shortDate(task.internal_due_date)}` : task.due_at ? `Due ${shortDate(task.due_at)}` : ''}</span>
    {task.statutory_due_date && <span className="block text-[10px] text-amber-700" data-testid={`task-ato-due-${task.id}`}>ATO due {shortDate(task.statutory_due_date)}</span>}
  </span>
);

// Server-side filtered + paginated list view with optional grouping. Page-level FilterBar values (urlFilters) are the source of truth for status/assignee/client/due/type.
const OPEN = 'todo,in_progress,waiting_client,in_review';
const toServer = (u, meId) => ({
  status: u.status === 'open' ? OPEN : u.status, client_id: u.client,
  assignee_id: u.assignee === 'me' ? meId : u.assignee === 'unassigned' ? undefined : u.assignee, unassigned: u.assignee === 'unassigned' || undefined,
  due_bucket: u.due, kind: u.type, completed: u.completed || 'today',
});
const OWNED = ['status', 'client_id', 'client_name', 'assignee_id', 'overdue'];
export const TaskListView = ({ templates, clients, users, onOpen, reloadKey, urlFilters = {}, meId }) => {
  const [filters, setFilters] = useState({});
  const [groupBy, setGroupBy] = useState('none');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 50 });
  const uKey = JSON.stringify(urlFilters);
  useEffect(() => setPage(1), [filters, groupBy, uKey]);
  useEffect(() => {
    const local = Object.fromEntries(Object.entries(filters).filter(([k]) => !OWNED.includes(k)));
    const h = setTimeout(() => taskAutomationAPI.search({ ...local, ...toServer(urlFilters, meId), group_by: groupBy === 'none' ? undefined : groupBy, page, page_size: 50 })
      .then((r) => setData(r.data)).catch(() => {}), 250);
    return () => clearTimeout(h);
  }, [filters, groupBy, page, reloadKey, uKey, meId]); // eslint-disable-line
  const groups = [];
  data.items.forEach((t) => {
    const k = groupBy === 'none' ? '' : groupKey(t, groupBy);
    const g = groups.find((x) => x.k === k) || (groups.push({ k, items: [] }) && groups[groups.length - 1]);
    g.items.push(t);
  });
  const pages = Math.max(1, Math.ceil(data.total / data.page_size));
  return (
    <Card><CardContent className="pt-6 space-y-4">
      <TaskFilters filters={filters} setFilters={setFilters} templates={templates} clients={clients} users={users} groupBy={groupBy} setGroupBy={setGroupBy} hide={OWNED} />
      <p className="text-xs text-muted-foreground" data-testid="task-list-total">{data.total} task{data.total === 1 ? '' : 's'}{groupBy !== 'none' ? ` · grouped by ${GROUPS[groupBy].toLowerCase()}` : ''}</p>
      {data.items.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground"><CheckSquare className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" /><p>No tasks match</p></div>
      ) : groups.map((g) => (
        <div key={g.k || 'all'} className="space-y-2" data-testid={`task-group-${g.k || 'all'}`}>
          {g.k && <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-2">{g.k} <span className="font-normal">({g.items.length})</span></p>}
          {g.items.map((task) => (
            <div key={task.id} className="flex flex-wrap items-center justify-between gap-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer" onClick={() => onOpen(task)} data-testid={`task-list-${task.id}`}>
              <div className="flex-1 min-w-[200px]">
                <h4 className="font-medium text-foreground flex items-center gap-1.5">{task.recurrence_rule_id && <Repeat className="w-3.5 h-3.5 text-primary" />}{task.title}</h4>
                <p className="text-xs text-muted-foreground">{[task.client_name, task.assignee_name].filter(Boolean).join(' · ')}</p>
              </div>
              <div className="flex items-center gap-3">
                <TaskProgress task={task} className="w-24" />
                <Badge variant="outline" data-testid={`task-list-status-${task.id}`}>{STATUS[task.status] || task.status}</Badge>
                {task.status === 'done' && task.completed_at && <span className="text-xs text-emerald-700 whitespace-nowrap" data-testid={`task-list-completed-${task.id}`}>Completed {shortDate(new Date(task.completed_at).toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' }))}</span>}
                <DueDates task={task} />
              </div>
            </div>))}
        </div>))}
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="task-page-prev">Previous</Button>
          <span data-testid="task-page-label">Page {page} of {pages}</span>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="task-page-next">Next</Button>
        </div>)}
    </CardContent></Card>
  );
};
